import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Import a custom AI icon - You can replace this with the actual icon component
// This is based on the AIAssistantIcon from your code
const AIAssistantIcon = ({ color = 'primary', ...props }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    {/* Outer circle */}
    <circle cx="12" cy="12" r="10" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1.5" />

    {/* Abstract AI wave pattern */}
    <path d="M7 14.5c1.5 1.5 3 1.5 4.5 0s3-1.5 4.5 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M7 11.5c1.5 1.5 3 1.5 4.5 0s3-1.5 4.5 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M7 8.5c1.5 1.5 3 1.5 4.5 0s3-1.5 4.5 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// Define the props for the AIHeader component
interface AIHeaderProps {
  // Basic content
  title?: string;
  description?: string;

  // Dialog content
  dialogTitle: string;
  dialogDescription: string;
  customDialogContent?: React.ReactNode;

  // Progress message shown during AI generation
  progressMessage: string;

  // Callback function that will be called when the user confirms in the dialog
  onGenerateConfirm: () => Promise<void>;

  // AI generation state
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;

  // Optional props
  generateButtonText?: string;
  cancelButtonText?: string;
  className?: string;
  disabled?: boolean;
  tooltip?: string;
}

// Component for formatting time as MM:SS
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

const AIHeader: React.FC<AIHeaderProps> = ({
  title,
  description,
  dialogTitle,
  dialogDescription,
  customDialogContent,
  progressMessage,
  onGenerateConfirm,
  isGenerating,
  setIsGenerating,
  generateButtonText = 'Generate',
  cancelButtonText = 'Cancel',
  tooltip = 'Generate with AI',
  className,
  disabled = false,
}) => {
  // State for dialog open/close
  const [dialogOpen, setDialogOpen] = useState(false);

  // State for timer
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Use a ref to store the start time to access its current value in the interval
  const startTimeRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<number | null>(null);

  // State for showing generation complete message
  const [generationComplete, setGenerationComplete] = useState(false);
  const [finalTime, setFinalTime] = useState(0);

  // Ref to track mounted state to avoid memory leaks
  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    // Explicitly set to true on mount to handle React 18 StrictMode dev double-invoke
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (timerIntervalRef.current !== null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // Effect to handle timer
  useEffect(() => {
    // Debug log for state transition (avoid referencing elapsedTime to keep deps clean)
    console.log('[AIHeader] isGenerating changed:', isGenerating, {
      hasStartTime: startTimeRef.current !== null,
    });

    // Start timer when generation begins
    if (isGenerating && startTimeRef.current === null) {
      console.log('[AIHeader] Timer starting');
      startTimeRef.current = Date.now();
      setElapsedTime(0); // Reset elapsed time when starting

      // Update elapsed time every second using the current value of startTimeRef
      const interval = window.setInterval(() => {
        if (isMounted.current && startTimeRef.current !== null) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          if (elapsed % 5 === 0) {
            console.log('[AIHeader] Timer tick (s):', elapsed);
          }
          setElapsedTime(elapsed);
        }
      }, 1000);

      timerIntervalRef.current = interval;
    }
    // Stop timer when generation ends
    else if (!isGenerating && startTimeRef.current !== null) {
      const total = Math.floor((Date.now() - startTimeRef.current) / 1000);
      console.log('[AIHeader] Timer stopping. Total seconds:', total);

      // Clear the interval
      if (timerIntervalRef.current !== null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Set final values
      setFinalTime(total);
      startTimeRef.current = null;

      // Show generation complete message
      setGenerationComplete(true);
    }
  }, [isGenerating]);

  // Handle dialog open
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Handle generate button click
  const handleGenerate = async () => {
    setDialogOpen(false);
    console.log('[AIHeader] Generate clicked. Setting isGenerating(true).');
    setIsGenerating(true);
    setGenerationComplete(false);

    try {
      console.log('[AIHeader] onGenerateConfirm starting...');
      await onGenerateConfirm();
      console.log('[AIHeader] onGenerateConfirm resolved.');
    } catch (error) {
      console.error('[AIHeader] Error during AI generation:', error);
    }
  };

  // Handle dismiss completion message
  const handleDismissComplete = () => {
    setGenerationComplete(false);
  };

  return (
    <Box className={className}>
      {/* Header with title and AI button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        {title &&
          <Typography variant="h6" gutterBottom>{title}</Typography>
        }
        <Tooltip title={title ? `Generate ${title} with AI` : (tooltip || '')}>
          <span> {/* Wrap with span to allow tooltip on disabled button */}
            <IconButton
              color="primary"
              onClick={handleOpenDialog}
              disabled={disabled || isGenerating}
              sx={{
                ml: 1,
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                }
              }}
              aria-label="Generate with AI"
            >
              {isGenerating ? (
                <CircularProgress size={24} color="primary" />
              ) : (
                <AIAssistantIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Description */}
      {description &&
        <Typography variant="body2" color="text.secondary" paragraph>
          {description}
        </Typography>
      }

      {/* AI Generation in progress alert */}
      {isGenerating && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {progressMessage}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Time elapsed: {formatTime(elapsedTime)}
          </Typography>
        </Alert>
      )}

      {/* Generation completed alert */}
      {generationComplete && !isGenerating && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Generation completed in {formatTime(finalTime)}!
          <IconButton
            size="small"
            onClick={handleDismissComplete}
            sx={{ ml: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      )}

      {/* Dialog for confirmation */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="ai-dialog-title"
        aria-describedby="ai-dialog-description"
      >
        <DialogTitle id="ai-dialog-title">
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="ai-dialog-description">
            {dialogDescription}
          </DialogContentText>
          {/* Render custom dialog content if provided */}
          {customDialogContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {cancelButtonText}
          </Button>
          <Button onClick={handleGenerate} color="primary" variant="contained">
            {generateButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIHeader;